import {useCallback, useEffect, useRef, useState} from '@wordpress/element';
import {useAppContext} from "./context/app-context";
import {toKebabCase} from "./lib/utils";
import {IconUrl, styles as stylesList} from "./lib/constants";
import {FixedSizeGrid as Grid} from 'react-window';

const IconsList = ({onChange, savedStyles}) => {
  const {
    categories,
    icons: rawIcons,
  } = useAppContext();

  const [style, setStyle] = useState(null);
  const [type, setType] = useState(null);
  const [selectedStyleIndex, setSelectedStyleIndex] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [search, setSearch] = useState("");

  const [selectedIcon, setSelectedIcon] = useState(null);

  const listContentRef = useRef(null);

  const [icons, setIcons] = useState([]);
  const [columnCount, setColumnCount] = useState(6); // Default to 3 columns
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(630);
  const [screenWidth, setScreenWidth] = useState(0);
  const gapSize = 10; // Tailwind's gap-4 (1rem = 16px)
  
  const styles = [
    'All',
    ...stylesList.filter(style => savedStyles.includes(style.id))
  ];

  useEffect(() => {
    // Ensure the element is rendered by checking if current is not null
    if (listContentRef.current) {
      const width = listContentRef.current.offsetWidth;
      const height = listContentRef.current.offsetHeight;

      // Calculate the number of columns
      const columnCount = Math.floor(width / 120);
      setColumnCount(columnCount);
      setContainerWidth(width - (gapSize * 2));
      setContainerHeight(height - 50);

      // Set the screen width
      setScreenWidth(width);
    }

    // Optional: Handle window resize
    const handleResize = () => {
      if (listContentRef.current) {
        const width = listContentRef.current.offsetWidth;
        const height = listContentRef.current.offsetHeight;

        setContainerWidth(width - (gapSize * 2));
        setContainerHeight(height);
        setScreenWidth(width);
      }
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const getData = setTimeout(() => {
      setSearch(searchQuery)
    }, 50)

    return () => clearTimeout(getData)
  }, [searchQuery])

  const iconsWithStyles = useCallback(() => {
    let styledIcons = [];

    if (!style && !type) {
      // Duplicate icons for each style
      // insert the duplicated of each icon after the original icon
      rawIcons.forEach((icon) => {
        styles.forEach((style) => {
          if (style !== "All") {
            styledIcons.push({
              ...icon,
              uuid: `${toKebabCase(icon?.name)}-${style?.title?.toLowerCase()}-${style?.type?.toLowerCase()}`,
              style: style.title,
              type: style.type,
            })
          }
        })
      })
    } else {
      rawIcons.forEach((icon) => {
        styledIcons.push({
          ...icon,
          uuid: `${toKebabCase(icon?.name)}-${style?.toLowerCase()}-${type?.toLowerCase()}`,
          style,
          type,
        })
      })
    }

    // Filter icons by category
    if (selectedCategory) {
      styledIcons = styledIcons.filter((icon) => icon?.category === selectedCategory?.slug)
    }

    // search by keyword in icon name and tags
    if (search) {
      styledIcons = styledIcons.filter((item) => {
        return item?.name?.toLowerCase().includes(search?.toLowerCase()) || item?.tags?.toLowerCase().includes(search?.toLowerCase())
      })

      // sort results if the keyword is found in the beginning of the icon name, then sort by name
      styledIcons = styledIcons.sort((a, b) => {
        const aName = a?.name?.toLowerCase()
        const bName = b?.name?.toLowerCase()

        if (aName?.startsWith(search) && !bName?.startsWith(search)) {
          return -1
        } else if (!aName?.startsWith(search) && bName?.startsWith(search)) {
          return 1
        } else {
          return aName?.localeCompare(bName)
        }
      })
    }

    return styledIcons;
  }, [rawIcons, style, type, selectedCategory, search]);

  useEffect(() => {
    setIcons(iconsWithStyles());
  }, [iconsWithStyles]);

  const handleSelectIcon = async (icon) => {
    setSelectedIcon(icon);

    if (icon) {
      onChange(icon);
    }
  }

  // Function to render each cell in the virtualized grid
  const cellRenderer = ({columnIndex, rowIndex, style}) => {
    const index = rowIndex * columnCount + columnIndex;
    const icon = icons[index];

    // Adjusting style to include gap
    const adjustedStyle = {
      ...style,
      top: style.top + gapSize,
      left: style.left + gapSize,
      width: style.width - gapSize,
      height: style.height - gapSize,
      paddingRight: gapSize,
    };

    if(!icon) return null;

    return (
      <div style={adjustedStyle} key={index}>
        <div
          className={`hugeicons-icon ${selectedIcon?.uuid === icon?.uuid ? "hugeicons-icon--selected" : ""}`}
          onClick={() => {
            handleSelectIcon(icon).finally()
          }}
        >
          <div className={"hugeicons-icon__image"}>
            <img src={IconUrl(icon?.uuid)} alt={icon?.name} width={28} height={28}/>
          </div>
        </div>

        <div className={"hugeicons-icon__name"}>
          {icon?.name}
        </div>
      </div>
    );
  };

  const columnWidth = (containerWidth + gapSize) / columnCount;

  return (
    <div className={"hugeicons-icons-list"}>
      <div className={"hugeicons-icons-list__content"} ref={listContentRef}>
        <div>
          <div className={"hugeicons-icons-list__content__filter"}>
            <div className={"hugeicons-icons-list__content__filter__search"}>
              <input
                type={"text"}
                placeholder={"Search icons"}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
              />
            </div>
            
            <div className={"hugeicons-icons-list__content__filter__dropdowns"}>
              <div className={"hugeicons-icons-list__content__filter__style"}>
                <select
                  value={selectedCategory?.id || ""}
                  onChange={(e) => {
                    const category = categories.find(cat => cat.id === parseInt(e.target.value));
                    setSelectedCategory(category || null);
                  }}
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={"hugeicons-icons-list__content__filter__style"}>
                <select
                  value={selectedStyleIndex || "0"}
                  onChange={(e) => {
                    const selectedStyle = styles[e.target.value];
                    setSelectedStyleIndex(e.target.value);
                    if (selectedStyle?.title === "All") {
                      setStyle(null);
                      setType(null);
                      return;
                    }
                    setStyle(selectedStyle?.title);
                    setType(selectedStyle?.type);
                  }}
                >
                  {styles.map((style, index) => (
                    <option key={index} value={index}>
                      {style.title || "All Styles"}
                      {style?.type ? ` (${style?.type})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className={"hugeicons-icons-list__content__grid-container"}>
          <Grid
            columnCount={columnCount}
            columnWidth={columnWidth}
            height={containerHeight}
            rowCount={Math.ceil(icons.length / columnCount)}
            rowHeight={columnWidth + (24)}
            width={screenWidth}
            overscanRowCount={10}
          >
            {cellRenderer}
          </Grid>
        </div>
      </div>
    </div>
  );

}

export default IconsList;